import React from 'react';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, startOfWeek, endOfWeek } from 'date-fns';

export default function CalendarMonthView({ currentDate, appointments, onAppointmentClick, onDateClick }) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const getAppointmentsForDay = (day) => {
    return appointments?.filter(apt => 
      isSameDay(new Date(apt.startTime), day)
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-700',
      confirmed: 'bg-green-100 text-green-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-gray-100 text-gray-700',
      cancelled: 'bg-red-100 text-red-700',
      no_show: 'bg-orange-100 text-orange-700'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    >
      {/* Weekday Headers */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
          <div
            key={day}
            className="px-4 py-3 text-center text-sm font-semibold text-gray-700 bg-gray-50"
          >
            {day}
          </div>
        ))}
      </div>
      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {days?.map((day, idx) => {
          const dayAppointments = getAppointmentsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={idx}
              onClick={() => onDateClick(day)}
              className={`min-h-[120px] border-r border-b border-gray-200 p-2 cursor-pointer hover:bg-gray-50 transition-colors ${
                !isCurrentMonth ? 'bg-gray-50' : 'bg-white'
              } ${idx % 7 === 6 ? 'border-r-0' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`text-sm font-medium ${
                    isToday
                      ? 'bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center'
                      : isCurrentMonth
                      ? 'text-gray-900' :'text-gray-400'
                  }`}
                >
                  {format(day, 'd')}
                </span>
                {dayAppointments?.length > 0 && (
                  <span className="text-xs text-gray-500 font-medium">
                    {dayAppointments?.length}
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {dayAppointments?.slice(0, 3)?.map((apt) => (
                  <div
                    key={apt?.id}
                    onClick={(e) => {
                      e?.stopPropagation();
                      onAppointmentClick(apt);
                    }}
                    className={`text-xs px-2 py-1 rounded cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(
                      apt?.status
                    )}`}
                  >
                    <div className="font-medium truncate">
                      {format(new Date(apt.startTime), 'h:mm a')}
                    </div>
                    <div className="truncate">{apt?.title}</div>
                    {apt?.customer && (
                      <div className="truncate text-xs opacity-75">
                        {apt?.customer?.company_name || apt?.customer?.contact_person}
                      </div>
                    )}
                  </div>
                ))}
                {dayAppointments?.length > 3 && (
                  <div className="text-xs text-gray-500 px-2">
                    +{dayAppointments?.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}